import type {DefaultError} from "@tanstack/react-query";
import type {UrlQueryKey} from "~/query/queryClient.ts";
import {useCustomQuery} from "~/query/queryClient.ts";
import type {ContractListParams} from "./useContractListParams.ts";
import {contractsUrl} from "./useContractExplorerQueries.ts";

/** `GET /api/data/{bagId}/contracts/{id}/neighbours/` */
export const contractNeighboursUrl = (bagId: number, contractId: number) =>
    `${contractsUrl(bagId)}${contractId}/neighbours/`;

export type ContractNeighbours = {
    previous_id: number | null;
    next_id: number | null;
    /** Position in the filtered list, or null when the list excludes it. */
    index: number | null;
    total: number;
};

/** The list params the endpoint needs to reproduce the list's row order. */
const neighbourParams = (params: ContractListParams) => ({
    ...params.search && {search: params.search},
    ...params.lifecycle && {lifecycle: params.lifecycle},
    ...params.type && {contract_type: params.type},
    ordering: params.ordering,
});

/**
 * Which contracts sit before and after this one **in the list's order** —
 * answered by the server, so stepping works even for a contract the grid has
 * not loaded (a deep link into the tail of a long list).
 *
 * `enabled` is meant to be false while the grid can answer it for free.
 */
export function useContractNeighboursQuery(
    bagId: number,
    contractId: number,
    params: ContractListParams,
    enabled: boolean,
) {
    return useCustomQuery<ContractNeighbours, DefaultError, ContractNeighbours, UrlQueryKey>({
        queryKey: [contractNeighboursUrl(bagId, contractId), {params: neighbourParams(params)}],
        enabled: enabled && bagId > 0 && contractId > 0,
        staleTime: 30_000,
    });
}
